import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { RangePreset } from '@/lib/analytics/metrics';

interface DateRangePickerProps {
  value: DateRange | undefined;
  onChange: (next: DateRange | undefined) => void;
  /** Called when the user clicks a preset button in the left rail. */
  onPreset?: (preset: RangePreset) => void;
  className?: string;
  placeholder?: string;
}

const PRESETS: { key: RangePreset; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: '7d',    label: 'Last 7 days' },
  { key: '30d',   label: 'Last 30 days' },
  { key: '90d',   label: 'Last 90 days' },
  { key: 'qtd',   label: 'Quarter to date' },
  { key: 'ytd',   label: 'Year to date' },
];

export function DateRangePicker({
  value,
  onChange,
  onPreset,
  className,
  placeholder = 'Pick a range',
}: DateRangePickerProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={cn('gap-2', className)}>
          <CalendarIcon className="h-4 w-4" />
          {value?.from
            ? value.to
              ? `${format(value.from, 'LLL d')} - ${format(value.to, 'LLL d, y')}`
              : format(value.from, 'LLL d, y')
            : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <div className="flex">
          {onPreset && (
            <div className="flex flex-col gap-1 border-r p-2 min-w-[120px]">
              {PRESETS.map((p) => (
                <Button
                  key={p.key}
                  variant="ghost"
                  size="sm"
                  className="justify-start"
                  onClick={() => onPreset(p.key)}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          )}
          <Calendar
            mode="range"
            selected={value}
            onSelect={onChange}
            numberOfMonths={2}
            defaultMonth={value?.from}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
